import { useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router";
import { useAuth } from "@/modules/auth";
import { usePageLoader } from "@/contexts/page-loader-context";

export default function OnboardingGuard() {
	const navigate = useNavigate();
	const location = useLocation();
	const { isBrandAvailable, isNameAvailable, loading } = useAuth();
	const { showLoader, hideLoader } = usePageLoader();
	const searchParams = new URLSearchParams(location.search);
	const redirect = searchParams.get("redirect");
	const redirectQuery = redirect ? `?redirect=${encodeURIComponent(redirect)}` : "";

	// Handle loader visibility
	useEffect(() => {
		const shouldShowLoader = loading || (!isNameAvailable && location.pathname !== "/onboarding/complete-profile") || (!isBrandAvailable && location.pathname !== "/onboarding/create-brand");

		if (shouldShowLoader) {
			showLoader();
		} else {
			hideLoader();
		}
	}, [loading, isNameAvailable, isBrandAvailable, location.pathname, showLoader, hideLoader]);

	useEffect(() => {
		// Wait for auth to finish loading
		if (loading) return;

		if (!isNameAvailable) {
			// User needs to complete their profile first
			if (location.pathname !== "/onboarding/complete-profile") {
				navigate(`/onboarding/complete-profile${redirectQuery}`, { replace: true });
			}
		} else if (!isBrandAvailable) {
			// User has profile but needs to create brand
			if (location.pathname !== "/onboarding/create-brand") {
				navigate(`/onboarding/create-brand${redirectQuery}`, { replace: true });
			}
		}
		// If onboarding is complete and user is on a regular page, do nothing (render children)
	}, [isBrandAvailable, isNameAvailable, loading, navigate, location]);

	// Show nothing while loading (loader is handled by context)
	if (loading) {
		return null;
	}

	// Show nothing while redirecting to complete profile
	if (!isNameAvailable && location.pathname !== "/onboarding/complete-profile") {
		return null;
	}

	// Show nothing while redirecting to create brand
	if (!isBrandAvailable && location.pathname !== "/onboarding/create-brand") {
		return null;
	}

	return <Outlet />;
}
